// RiceScoring - RICE framework scoring interface
// Implements US-3.2: RICE Scoring Interface and Calculation

import React, { useState, useEffect, useCallback } from 'react';
import styles from './RiceScoring.module.css';
import { Item, Score, CalculatedResult } from '../types/domain';
import { updateScores } from '../services/scoreService';

interface RiceScoringProps {
  item: Item;
  isReadOnly: boolean;
  onScoresUpdated?: (itemId: string, scores: Score[], result: CalculatedResult) => void;
}

/**
 * RICE scoring component
 * - Displays 4 input fields: Reach, Impact, Confidence, Effort
 * - Calculates and displays RICE score: (R × I × C) / E
 * - Auto-saves on value change with debouncing
 */
export const RiceScoring: React.FC<RiceScoringProps> = ({ item, isReadOnly, onScoresUpdated }) => {
  // EXTRACT current scores from item
  const getScoreValue = (criterion: string): number => {
    const score = item.scores?.find((s) => s.criterion === criterion);
    return score ? Number(score.value) : 0;
  };

  // STATE for score inputs
  const [reach, setReach] = useState<number>(getScoreValue('reach'));
  const [impact, setImpact] = useState<number>(getScoreValue('impact'));
  const [confidence, setConfidence] = useState<number>(getScoreValue('confidence'));
  const [effort, setEffort] = useState<number>(getScoreValue('effort'));
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // UPDATE local state when item scores change (real-time updates from other users)
  useEffect(() => {
    setReach(getScoreValue('reach'));
    setImpact(getScoreValue('impact'));
    setConfidence(getScoreValue('confidence'));
    setEffort(getScoreValue('effort'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.scores]);

  /**
   * CALCULATE RICE score locally for immediate feedback
   * Formula: (Reach × Impact × Confidence) / Effort
   * NOTE: No useCallback needed - function is only called inline, not passed as prop
   */
  const calculateScore = (r: number, i: number, c: number, e: number): number | null => {
    if (r > 0 && i > 0 && c > 0 && e > 0) {
      return (r * i * c) / e;
    }
    return null;
  };

  const calculatedScore = calculateScore(reach, impact, confidence, effort);

  /**
   * SAVE scores to backend
   * NOTE: Debounced to avoid excessive API calls
   */
  const saveScores = useCallback(
    async (r: number, i: number, c: number, e: number) => {
      // VALIDATE: Only save if all values are set
      if (r === 0 || i === 0 || c === 0 || e === 0) {
        return;
      }

      try {
        setIsSaving(true);
        setError(null);

        const response = await updateScores(item.id, [
          { criterion: 'reach', value: r },
          { criterion: 'impact', value: i },
          { criterion: 'confidence', value: c },
          { criterion: 'effort', value: e },
        ]);

        if (!response.success) {
          throw new Error(response.error || 'Failed to save scores');
        }

        // NOTIFY parent component of score update
        if (onScoresUpdated && response.scores && response.calculatedResult) {
          onScoresUpdated(item.id, response.scores, response.calculatedResult);
        }
      } catch (err) {
        console.error('Failed to save scores:', err);
        setError(err instanceof Error ? err.message : 'Failed to save scores');
      } finally {
        setIsSaving(false);
      }
    },
    [item.id, onScoresUpdated]
  );

  /**
   * DEBOUNCE score saving (wait 1 second after last change)
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isReadOnly && reach > 0 && impact > 0 && confidence > 0 && effort > 0) {
        saveScores(reach, impact, confidence, effort);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [reach, impact, confidence, effort, isReadOnly, saveScores]);

  /**
   * HANDLE input change with validation
   */
  const handleInputChange = (
    criterion: 'reach' | 'impact' | 'confidence' | 'effort',
    value: string
  ) => {
    const numValue = parseInt(value, 10);

    // VALIDATE: Empty input
    if (value === '') {
      switch (criterion) {
        case 'reach':
          setReach(0);
          break;
        case 'impact':
          setImpact(0);
          break;
        case 'confidence':
          setConfidence(0);
          break;
        case 'effort':
          setEffort(0);
          break;
      }
      return;
    }

    // VALIDATE: Must be number between 1-10
    if (isNaN(numValue) || numValue < 1 || numValue > 10) {
      return;
    }

    // UPDATE state
    switch (criterion) {
      case 'reach':
        setReach(numValue);
        break;
      case 'impact':
        setImpact(numValue);
        break;
      case 'confidence':
        setConfidence(numValue);
        break;
      case 'effort':
        setEffort(numValue);
        break;
    }
  };

  return (
    <div className={styles.riceScoring}>
      <div className={styles.criteriaGrid}>
        {/* REACH input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`reach-${item.id}`}>Reach</label>
          <input
            id={`reach-${item.id}`}
            type="number"
            min="1"
            max="10"
            value={reach || ''}
            onChange={(e) => handleInputChange('reach', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-10"
            className={styles.input}
          />
        </div>

        {/* IMPACT input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`impact-${item.id}`}>Impact</label>
          <input
            id={`impact-${item.id}`}
            type="number"
            min="1"
            max="10"
            value={impact || ''}
            onChange={(e) => handleInputChange('impact', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-10"
            className={styles.input}
          />
        </div>

        {/* CONFIDENCE input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`confidence-${item.id}`}>Confidence</label>
          <input
            id={`confidence-${item.id}`}
            type="number"
            min="1"
            max="10"
            value={confidence || ''}
            onChange={(e) => handleInputChange('confidence', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-10"
            className={styles.input}
          />
        </div>

        {/* EFFORT input */}
        <div className={styles.criteriaField}>
          <label htmlFor={`effort-${item.id}`}>Effort</label>
          <input
            id={`effort-${item.id}`}
            type="number"
            min="1"
            max="10"
            value={effort || ''}
            onChange={(e) => handleInputChange('effort', e.target.value)}
            disabled={isReadOnly}
            placeholder="1-10"
            className={styles.input}
          />
        </div>
      </div>

      {/* CALCULATED SCORE display */}
      {calculatedScore !== null && (
        <div className={styles.scoreDisplay}>
          <span className={styles.scoreLabel}>RICE Score:</span>
          <span className={styles.scoreValue}>{calculatedScore.toFixed(2)}</span>
          {isSaving && <span className={styles.savingIndicator}>Speichere...</span>}
        </div>
      )}

      {/* ERROR message */}
      {error && <div className={styles.errorMessage}>{error}</div>}

      {/* UNSCORED indicator */}
      {calculatedScore === null && !isReadOnly && (
        <div className={styles.unscoredMessage}>Gib alle Kriterien ein, um den Score zu berechnen</div>
      )}
    </div>
  );
};
